import { useEffect, useState } from 'react';
import { audioPlayer, type PlayerState, type QueueItem } from './AudioPlayer';

/** Subscribe to the audio player's state. Re-renders on any change. */
export function useAudioState(): PlayerState {
  const [state, setState] = useState<PlayerState>(() => audioPlayer.getState());
  useEffect(() => audioPlayer.subscribe(setState), []);
  return state;
}

/**
 * Convenience hook: stable functions for `play`/`stop`/`playQueue` plus
 * helpers to check whether a particular id (or queue) is currently active.
 */
export function useAudio() {
  const state = useAudioState();
  return {
    state,
    play: (id: string, filename: string) => audioPlayer.play(id, filename),
    stop: () => audioPlayer.stop(),
    playQueue: (queueId: string, items: QueueItem[], gap?: number) =>
      audioPlayer.playQueue(queueId, items, gap),
    isPlaying: (id: string) =>
      state.playingId === id && (state.status === 'playing' || state.status === 'loading'),
    isLoading: (id: string) => state.playingId === id && state.status === 'loading',
    isQueueRunning: (queueId: string) => state.queueId === queueId,
  };
}
